import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchRoleMapSyncStatus } from '@/api/console';

/**
 * EAA-1817 (2026-08-25, user report: a Level assignment never reached ACH).
 *
 * WHAT THIS ANSWERS. `PROVISIONING_ENABLED` is the master switch for writing
 * permissions into other applications. When it is off, `provisioning.apply`
 * refuses every push and `try_provision` swallows the refusal by design (dark
 * shipping) — so the Assignment keeps `sync_status: 'pending'` and an EMPTY
 * `last_sync_error`, forever. On screen that is identical to "queued, running
 * shortly". The reporter had 132 grants in that state and no way to tell.
 *
 * WHY THE FLAG CANNOT BE READ FROM THE ENVIRONMENT. A confirmed console override
 * beats the env var, and that is precisely the case that bit here: the container
 * had `PROVISIONING_ENABLED=true` while the effective value was `false`. Only the
 * server can say which one won, so this reads the value it reports.
 *
 * WHY IT REUSES THE SYNC-STATUS QUERY. That payload is the console's existing
 * "is the role map healthy" fetch and already carries the flag, so every screen
 * that needs the answer shares one cached response instead of adding a request.
 * The key matches `useRoleMapTable`'s so the two genuinely share it.
 */
export interface ProvisioningSwitch {
    /** `false` only when the server has positively said so — never on a failed read. */
    off: boolean;
    /** Live grants waiting on the switch; 0 when it is on or unknown. */
    pendingCount: number;
}

export function useProvisioningSwitch(): ProvisioningSwitch {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const can = useCan();

    // The endpoint needs `console.rolemap.view`. A reader without it gets no
    // answer, and "no answer" must not render as "provisioning is off" — an
    // unverified warning about someone else's configuration is worse than silence.
    const enabled = Boolean(accessToken) && can('console.rolemap.view');

    const query = useQuery({
        queryKey: ['role-map-sync-status', accessToken],
        queryFn: () => fetchRoleMapSyncStatus(config.apiBase, accessToken as string),
        enabled,
        retry: false,
        staleTime: 60_000,
    });

    const data = query.data;
    return {
        off: data ? !data.provisioning_enabled : false,
        pendingCount: data?.pending_assignment_count ?? 0,
    };
}
